import {
  SearchCandidatesForCandidateBatchQuery,
  useOnCreateCandidateSubscription,
  useOnUpdateCandidateSubscription,
  useSearchCandidatesForCandidateBatchQuery
} from 'generated-graphql/hooks/apollo-hooks';
import {
  SearchableCandidateFilterInput,
  SearchableCandidateSortInput
} from 'generated-graphql/types';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import { useMemo, useEffect, useRef } from 'react';
import { CandidatesByCandidateBatchItem } from 'modules/analyse/types/CandidatesByCandidateBatch';
import { AnalyseTabsValue } from 'modules/analyse/constants/tabsValue';
import { sanitizeObject } from 'utils/fomatting';
import { ApolloListItemType } from 'core/types/ApolloListQuery';

type Props = {
  candidateBatchId: string | undefined;
  filter: SearchableCandidateFilterInput;
  sort: SearchableCandidateSortInput;
  limit: number;
  from: number;
  currentTab: AnalyseTabsValue;
  isRecalculatingScores: boolean;
};

type SearchCandidate =
  ApolloListItemType<SearchCandidatesForCandidateBatchQuery>;

type TabData = {
  items: CandidatesByCandidateBatchItem[];
  total: number;
  loading: boolean;
};

export const useAnalyseDetailsCandidates = ({
  candidateBatchId,
  filter,
  sort,
  limit,
  from,
  currentTab,
  isRecalculatingScores
}: Props) => {
  const {
    loading: lockedLoading,
    data: lockedData,
    updateQuery: updateLockedQuery,
    refetch: refetchLockedData
  } = useSearchCandidatesForCandidateBatchQuery({
    variables: {
      filter: { ...filter, isLocked: { eq: true } },
      sort: [sort],
      limit,
      from: 0
    },
    skip: !candidateBatchId
  });

  const {
    loading: withRequirementsLoading,
    data: withRequirementsData,
    updateQuery: updateWithRequirementsQuery,
    refetch: refetchWithRequirementsData
  } = useSearchCandidatesForCandidateBatchQuery({
    variables: {
      filter: { ...filter, isAllRequirementsMet: { eq: true } },
      sort: [sort],
      limit,
      from:
        currentTab === AnalyseTabsValue.CANDIDATES_WITH_MINIMUM_REQUIREMENTS
          ? from
          : 0
    },
    skip: !candidateBatchId
  });

  const {
    loading: allApplicantsLoading,
    data: allApplicantsData,
    updateQuery: updateAllApplicantsQuery,
    refetch: refetchWithoutRequirementsData
  } = useSearchCandidatesForCandidateBatchQuery({
    variables: {
      filter,
      sort: [sort],
      limit,
      from: currentTab === AnalyseTabsValue.ALL_APPLICANTS ? from : 0
    },
    skip: !candidateBatchId
  });

  const { error: createError } = useOnCreateCandidateSubscription({
    variables: {
      filter: {
        candidateBatchId: { eq: candidateBatchId as string }
      }
    },
    skip: !candidateBatchId,
    onData: async ({ data: { data } }) => {
      const candidateCreated = data?.onCreateCandidate;
      if (!candidateCreated) return;

      const sanitizedCandidateCreated =
        sanitizeObject<SearchCandidate>(candidateCreated);

      const addCandidateToQuery = (
        updateQueryFn:
          | typeof updateWithRequirementsQuery
          | typeof updateAllApplicantsQuery
          | typeof updateLockedQuery
      ) => {
        updateQueryFn((prevData) => {
          if (!prevData?.searchCandidates?.items) return prevData;

          if (
            prevData.searchCandidates.items.some(
              (candidate) => candidate?.id === sanitizedCandidateCreated.id
            )
          ) {
            return prevData;
          }

          return {
            ...prevData,
            searchCandidates: {
              ...prevData.searchCandidates,
              items: [
                ...prevData.searchCandidates.items,
                sanitizedCandidateCreated
              ],
              total: (prevData.searchCandidates.total || 0) + 1
            }
          };
        });
      };

      addCandidateToQuery(updateLockedQuery);
      addCandidateToQuery(updateWithRequirementsQuery);
      addCandidateToQuery(updateAllApplicantsQuery);
    }
  });

  const { error: updateError } = useOnUpdateCandidateSubscription({
    variables: {
      filter: {
        candidateBatchId: { eq: candidateBatchId as string }
      }
    },
    skip: !candidateBatchId,
    onData: async ({ data: { data } }) => {
      const candidateUpdated = data?.onUpdateCandidate;

      if (!candidateUpdated) return;

      const sanitizedCandidateUpdated =
        sanitizeObject<SearchCandidate>(candidateUpdated);

      const updateCandidateInQuery = (
        updateQueryFn:
          | typeof updateWithRequirementsQuery
          | typeof updateAllApplicantsQuery
          | typeof updateLockedQuery
      ) => {
        updateQueryFn((prevData) => {
          if (!prevData?.searchCandidates?.items) return prevData;

          const candidateIndex = prevData.searchCandidates.items.findIndex(
            (candidate) => candidate?.id === sanitizedCandidateUpdated.id
          );

          if (candidateIndex === -1) return prevData;

          const updatedItems = [...prevData.searchCandidates.items];
          updatedItems[candidateIndex] = {
            ...updatedItems[candidateIndex],
            ...sanitizedCandidateUpdated,
            // `sanitizeObject` removes nulls, but null clears a previous
            // analysis failure after a successful retry.
            analysisErrorCode: candidateUpdated.analysisErrorCode ?? null
          };

          return {
            ...prevData,
            searchCandidates: {
              ...prevData.searchCandidates,
              items: updatedItems
            }
          };
        });
      };

      updateCandidateInQuery(updateLockedQuery);
      updateCandidateInQuery(updateWithRequirementsQuery);
      updateCandidateInQuery(updateAllApplicantsQuery);
    }
  });

  // Track previous recalculation state
  const prevRecalculatingScores = useRef(isRecalculatingScores);

  // Effect to detect when recalculation completes
  useEffect(() => {
    const wasRecalculating = prevRecalculatingScores.current;
    // Update ref first to avoid repeated triggers on re-renders
    prevRecalculatingScores.current = isRecalculatingScores;

    if (wasRecalculating && !isRecalculatingScores && candidateBatchId) {
      const timer = setTimeout(() => {
        void Promise.all([
          refetchLockedData(),
          refetchWithRequirementsData(),
          refetchWithoutRequirementsData()
        ]);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isRecalculatingScores, candidateBatchId]);

  const lockedTabData: TabData = useMemo(() => {
    return {
      items: extractQueryListItems(lockedData),
      total: lockedData?.searchCandidates?.total || 0,
      loading: lockedLoading
    };
  }, [lockedData, lockedLoading]);

  const withRequirementsTabData: TabData = useMemo(() => {
    return {
      items: extractQueryListItems(withRequirementsData),
      total: withRequirementsData?.searchCandidates?.total || 0,
      loading: withRequirementsLoading
    };
  }, [withRequirementsData, withRequirementsLoading]);

  const allApplicantsTabData: TabData = useMemo(() => {
    return {
      items: extractQueryListItems(allApplicantsData),
      total: allApplicantsData?.searchCandidates?.total || 0,
      loading: allApplicantsLoading
    };
  }, [allApplicantsData, allApplicantsLoading]);

  // Current tab data based on active tab
  const currentTabData: TabData = useMemo(() => {
    switch (currentTab) {
      case AnalyseTabsValue.ALL_APPLICANTS:
        return allApplicantsTabData;
      case AnalyseTabsValue.CANDIDATES_WITH_MINIMUM_REQUIREMENTS:
        return withRequirementsTabData;
      case AnalyseTabsValue.SOURCE_CANDIDATES:
        return {
          items: [],
          total: 0,
          loading: false
        };
      default:
        return withRequirementsTabData;
    }
  }, [
    currentTab,
    lockedTabData,
    withRequirementsTabData,
    allApplicantsTabData
  ]);

  const tabCounts = useMemo(() => {
    return {
      allApplicants: allApplicantsTabData.total,
      withRequirements: withRequirementsTabData.total,
      locked: lockedTabData.total
    };
  }, [
    lockedTabData.total,
    withRequirementsTabData.total,
    allApplicantsTabData.total
  ]);

  return {
    currentTabData,
    tabCounts,
    subscriptionError: createError || updateError
  };
};
